import type { PortableTextBlock } from '@portabletext/types';

import type {
  PublishedLineup,
  PublishedLineupPlayer,
  TacticalLane,
  TacticalLine
} from '@api-service/predicted-lineups';
import type { TeamWithFixturePreview } from '@api-service/team-fixtures-preview';
import type { ArticleHeading } from '@lib/article-blocks/headings';
import { toSlug } from '@lib/page-utils/player-profile/slugs';
import type { PlayerFacts } from '@lib/predicted-lineups/player-facts';
import type { SanityPredictedLineupTeamSection } from '@lib/sanity';

/**
 * What the page needs to introduce a club: enough for the heading and the
 * meta line. A Sanity section satisfies it, and so does a fixture from the
 * public API, which is how gated clubs get a heading and a pitch without any
 * of their words reaching the static page.
 */
export type ClubDescriptor = {
  teamShortName: string;
  teamName?: string;
  opponentShortName?: string;
  opponentName?: string;
  isHome?: boolean;
  kickoffAt?: string;
};

export type ClubHeading = ArticleHeading & { teamShortName: string };

const HOME_MARKER = 'H';
const AWAY_MARKER = 'A';
const FALLBACK_HEADING_ID = 'club';
const CLUB_SORT_LOCALE = 'en-GB';

const clubName = (club: ClubDescriptor) => club.teamName ?? club.teamShortName;

const compareKickoff = (a: ClubDescriptor, b: ClubDescriptor) => {
  if (a.kickoffAt !== b.kickoffAt) {
    if (!a.kickoffAt) return 1;
    if (!b.kickoffAt) return -1;
    return a.kickoffAt.localeCompare(b.kickoffAt);
  }
  if (a.isHome !== b.isHome) return a.isHome ? -1 : 1;
  return a.teamShortName.localeCompare(b.teamShortName);
};

/**
 * A to Z by the name the heading shows, so readers find their club where they
 * expect it. A club with a double gameweek keeps its fixtures in kick-off order.
 */
export const compareClubs = (a: ClubDescriptor, b: ClubDescriptor) =>
  clubName(a).localeCompare(clubName(b), CLUB_SORT_LOCALE) ||
  compareKickoff(a, b);

export const buildClubHeadingText = (club: ClubDescriptor): string => {
  const name = clubName(club);
  if (!club.opponentShortName) return `${name} predicted lineup`;
  const opponent = club.opponentName ?? club.opponentShortName;
  const venue = club.isHome === false ? AWAY_MARKER : HOME_MARKER;
  return `${name} predicted lineup: ${opponent} (${venue})`;
};

/** Ids are de-duplicated the same way article headings are, so anchors stay unique. */
export const buildClubHeadings = (clubs: ClubDescriptor[]): ClubHeading[] => {
  const seen = new Map<string, number>();
  return clubs.map((club) => {
    const text = buildClubHeadingText(club);
    const base = toSlug(text) || FALLBACK_HEADING_ID;
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    return {
      id: count === 0 ? base : `${base}-${count + 1}`,
      text,
      teamShortName: club.teamShortName
    };
  });
};

/**
 * One descriptor per club per fixture in the gameweek, A to Z by club, from
 * the public fixtures preview.
 */
export const buildClubFixtures = (
  teams: TeamWithFixturePreview[],
  gameweek: number
): ClubDescriptor[] =>
  teams
    .flatMap((team) =>
      team.fixtures
        .filter((fixture) => fixture.gameweek === gameweek)
        .map((fixture) => ({
          teamShortName: team.shortName,
          teamName: team.name,
          opponentShortName: fixture.opponent.shortName,
          opponentName: fixture.opponent.name,
          isHome: fixture.venue === 'home',
          kickoffAt: fixture.kickoffAt ?? undefined
        }))
    )
    .sort(compareClubs);

/**
 * Clubs playing this gameweek that have no free section: the gated half of
 * the page. Their pitch is public; their words come from the premium read.
 */
export const gatedClubsFor = (
  clubFixtures: ClubDescriptor[],
  freeSections: SanityPredictedLineupTeamSection[]
): ClubDescriptor[] => {
  const free = new Set(freeSections.map((section) => section.teamShortName));
  return clubFixtures.filter((club) => !free.has(club.teamShortName));
};

/**
 * A scaffolded section already carries its fixture; a hand-made one may not,
 * so the fixture fills any blanks for the heading and meta line.
 */
export const describeSection = (
  section: SanityPredictedLineupTeamSection,
  clubFixtures: ClubDescriptor[]
): ClubDescriptor => {
  const fixture = clubFixtures.find(
    (club) => club.teamShortName === section.teamShortName
  );
  return {
    teamShortName: section.teamShortName,
    teamName: section.teamName ?? fixture?.teamName,
    opponentShortName: section.opponentShortName ?? fixture?.opponentShortName,
    opponentName: section.opponentName ?? fixture?.opponentName,
    isHome: section.isHome ?? fixture?.isHome,
    kickoffAt: section.kickoffAt ?? fixture?.kickoffAt
  };
};

export type DescribedSection = {
  section: SanityPredictedLineupTeamSection;
  club: ClubDescriptor;
};

/**
 * Sections A to Z by club, whatever order the editor left them in the Studio.
 */
export const describeSections = (
  sections: SanityPredictedLineupTeamSection[],
  clubFixtures: ClubDescriptor[]
): DescribedSection[] =>
  sections
    .map((section) => ({
      section,
      club: describeSection(section, clubFixtures)
    }))
    .sort((a, b) => compareClubs(a.club, b.club));

export const findLineupForClub = (
  lineups: PublishedLineup[],
  teamShortName: string
): PublishedLineup | undefined =>
  lineups.find((lineup) => lineup.teamShortName === teamShortName);

/** The later of the editor's stamp on the words and the API's stamp on the eleven. */
export const resolveLastUpdated = (
  section: Pick<SanityPredictedLineupTeamSection, 'updatedAt'> | undefined,
  lineup: Pick<PublishedLineup, 'editedAt'> | undefined
): string | undefined => {
  const stamps = [section?.updatedAt, lineup?.editedAt].filter(
    (stamp): stamp is string => Boolean(stamp)
  );
  if (stamps.length === 0) return undefined;
  return [...stamps].sort().at(-1);
};

// Top of the pitch first, so the goalkeeper renders at the bottom.
const LINE_ORDER: TacticalLine[] = ['FWD', 'AM', 'MID', 'DM', 'DEF', 'GK'];
// Viewed from behind the goalkeeper: the left wing sits on the left.
const LANE_ORDER: TacticalLane[] = [
  'WIDE_LEFT',
  'CENTRE_LEFT',
  'CENTRE',
  'CENTRE_RIGHT',
  'WIDE_RIGHT'
];
const POSITION_TO_LINE: Record<string, TacticalLine> = {
  GK: 'GK',
  DEF: 'DEF',
  MID: 'MID',
  FWD: 'FWD'
};
const FALLBACK_LINE: TacticalLine = 'MID';
const FALLBACK_LANE: TacticalLane = 'CENTRE';

/**
 * Rows of starters from the API's tactical roles, top of the pitch first. A
 * player without a role falls back to the line their position implies.
 */
export const buildFormationRows = (
  players: PublishedLineupPlayer[]
): PublishedLineupPlayer[][] => {
  const byLine = new Map<TacticalLine, PublishedLineupPlayer[]>();
  for (const player of players) {
    if (!player.isStarter) continue;
    const line =
      player.tacticalRole?.line ??
      POSITION_TO_LINE[player.position] ??
      FALLBACK_LINE;
    byLine.set(line, [...(byLine.get(line) ?? []), player]);
  }
  return LINE_ORDER.map((line) =>
    [...(byLine.get(line) ?? [])].sort(
      (a, b) =>
        LANE_ORDER.indexOf(a.tacticalRole?.lane ?? FALLBACK_LANE) -
        LANE_ORDER.indexOf(b.tacticalRole?.lane ?? FALLBACK_LANE)
    )
  ).filter((row) => row.length > 0);
};

export const flattenNotes = (
  sections: SanityPredictedLineupTeamSection[]
): PortableTextBlock[] =>
  sections.flatMap((section) => (section.notes ?? []) as PortableTextBlock[]);

/**
 * Clearing a rich text field in the Studio leaves an empty paragraph behind
 * rather than unsetting it, so "has blocks" is not the same as "has words".
 * Anything that is not a text block (an image, a ticker) counts as content.
 */
export const hasPortableTextContent = (
  blocks: { _type?: string; children?: { text?: string }[] }[] | undefined
): boolean =>
  (blocks ?? []).some(
    (block) =>
      block._type !== 'block' ||
      (block.children ?? []).some((child) => child.text?.trim())
  );

/** Every player whose headshot the static page can show: the elevens plus the free clubs' lists. */
export const collectHeadshotOptaIds = (
  sections: SanityPredictedLineupTeamSection[],
  lineups: PublishedLineup[]
): string[] => {
  const ids = new Set<string>();
  for (const lineup of lineups) {
    for (const player of lineup.players) ids.add(player.playerId);
  }
  for (const section of sections) {
    for (const entry of [
      ...(section.ruledOut ?? []),
      ...(section.doubts ?? []),
      ...(section.suspensions ?? [])
    ]) {
      ids.add(entry.player.optaUuid);
    }
  }
  return [...ids];
};

const AVAILABLE_STATUS = 'a';
const FULL_CHANCE_OF_PLAYING = 100;

const NO_PLAYERS: ReadonlySet<string> = new Set();

/**
 * A starter is flagged when the editor lists them as a doubt, or when the
 * FPL feed says they are anything other than fully available, unless the
 * editor hid that flag because they expect the player to start.
 */
export const isDoubtfulStarter = (
  player: Pick<PublishedLineupPlayer, 'playerId'>,
  facts: PlayerFacts | undefined,
  editorDoubtOptaIds: ReadonlySet<string>,
  editorClearedOptaIds: ReadonlySet<string> = NO_PLAYERS
): boolean => {
  if (editorDoubtOptaIds.has(player.playerId)) return true;
  if (editorClearedOptaIds.has(player.playerId)) return false;
  if (!facts) return false;
  if (facts.status !== AVAILABLE_STATUS) return true;
  return (
    facts.chanceOfPlaying !== null &&
    facts.chanceOfPlaying < FULL_CHANCE_OF_PLAYING
  );
};
